import { Active, Over } from "@dnd-kit/core";
import { isNumber } from "@memento/library/type-witchcraft";

type GetNewSortOrderProps = {
  droppedItemOldSortOrder: number;
  droppedItemNewSortOrder: number;
  thisItemOldSortOrder: number;
};

// NOTE: All of the sortOrders must be 0-based
export function getNewSortOrderAfterDrop({
  // the dropped item is the item that was dragged and dropped
  droppedItemOldSortOrder,
  droppedItemNewSortOrder,
  // this is the item we're trying to figure out the new sortOrder for, based on
  // the movement of the dropped item
  thisItemOldSortOrder,
}: GetNewSortOrderProps): number {
  // "this" item is the item that is being dragged/dropped
  if (thisItemOldSortOrder === droppedItemOldSortOrder) {
    // move "this" item to its new location
    return droppedItemNewSortOrder;
  } else if (
    // "this" item was originally "after" the "dropped" item
    thisItemOldSortOrder > droppedItemOldSortOrder &&
    // "this" item is now "before" or at the new location of the "dropped" item
    thisItemOldSortOrder <= droppedItemNewSortOrder
  ) {
    // the item needs to be moved "back" by one
    return thisItemOldSortOrder - 1;
  } else if (
    // "this" item was originally "before" the "dropped" item
    thisItemOldSortOrder < droppedItemOldSortOrder &&
    // "this" item is now "after" or at the new location of the "dropped" item
    thisItemOldSortOrder >= droppedItemNewSortOrder
  ) {
    // "this" item needs to be moved "forward" by one
    return thisItemOldSortOrder + 1;
  } else {
    // "this" item is already in the position it needs to be in
    return thisItemOldSortOrder;
  }
}

type OnDragEndProps = {
  oldIndex: number;
  newIndex: number;
};

type OnDragEnd = ({ oldIndex, newIndex }: OnDragEndProps) => void;

export function handleVideoDragEnd(
  onDragEndMoved: OnDragEnd,
  onDragEndUnmoved: () => void
) {
  return ({ active, over }: { active: Active; over: Over | null }) => {
    // From dnd-kit: If there are no collisions detected when the draggable item
    // is dropped, the over property will be null. If a collision is detected,
    // the over property will contain the id of the droppable over which it was
    // dropped.
    if (!over) {
      // No collision detected
      onDragEndUnmoved();
      return;
    }

    if (active.id === over.id) {
      // No sort change
      onDragEndUnmoved();
      return;
    }

    const oldIndex = active.data.current?.sortable.index;
    const newIndex = over.data.current?.sortable.index;

    if (!isNumber(oldIndex) || !isNumber(newIndex)) {
      // In case the data refs are not set
      onDragEndUnmoved();
      return;
    }

    onDragEndMoved({ oldIndex, newIndex });
  };
}
